// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Stage 1 of the Power Flow model: which assets are on the diagram, how they
 * are connected, and what the payload failed to tell us.
 *
 * See docs/power-flow/power-flow-single-line-diagram.md §7.1.
 *
 * The organising principle here is the departure from the old Asset Topology:
 * nothing in this module looks at entity type to decide *whether* an asset
 * appears or *where* it goes. Membership is "does it have a power connection",
 * derived from the relationships; type is consulted only to say how the asset
 * is drawn, and to judge whether a root or a leaf is plausible.
 *
 * Diagnostics are a first-class output rather than a debug aid. Every way this
 * can produce a thinner diagram than the site really has — an asset with no
 * published `feeds`, an edge pointing at an entity that is not in the payload,
 * a predicate the ontology does not recognise, topology carried in `attributes`
 * instead of `relationships[]` — is counted and rendered. A one-line that
 * quietly omits a conductor is worse than one that says it is incomplete.
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import { cleanEntityType, getTelemetryEntityIds, isAssetEntityType } from './entityHelpers';
import {
  buildFlowAdjacency,
  isFlowRelationshipType,
  isFluidRelationshipType,
  normalizeFlowRelationship,
  type FlowAdjacency,
} from './flowRelationships';
import { isKnownRelationshipType } from './ontologyAccess';
import { parseQuantity } from './quantity';
import {
  hasSymbolRule,
  isBusTerminalType,
  isNonElectricalType,
  isSpaceType,
  symbolRuleFor,
  type PowerRole,
  type PowerSymbol,
  type VoltageClass,
} from '../config/powerFlowSymbols';

/** An asset on the diagram, before it has a rank or a position. */
export interface PowerFlowVertex {
  id: string;
  entityType: string;
  role: PowerRole;
  symbol: PowerSymbol;
  voltageClass: VoltageClass;
  /** Upstream neighbours **that are also on the diagram**, sorted. */
  in: string[];
  /** Downstream neighbours that are also on the diagram, sorted. */
  out: string[];
}

/** A conductor between two on-diagram assets, pointing downstream. */
export interface PowerFlowLink {
  /** `${source}->${target}`; one link per ordered pair however many
   *  relationships expressed it. */
  id: string;
  source: string;
  target: string;
  /** Canonical forward types that produced it, sorted. */
  relTypes: string[];
  /**
   * Bus terminals (breakers) the conductor was folded through, sorted. Empty
   * for a conductor the payload published board-to-asset directly.
   */
  via?: string[];
}

/** Type-level breakdown, for the "N assets have no power connections" line. */
export interface TypeCount {
  entityType: string;
  count: number;
}

/**
 * What the payload could not tell us. Every field is rendered somewhere —
 * see `PowerFlowNotices` in the page.
 */
export interface PowerFlowDiagnostics {
  /** Asset entities with no published power relationship at all. */
  unconnected: TypeCount[];
  unconnectedCount: number;
  /** Flow edges naming an entity absent from `entities[]`. */
  danglingLinkCount: number;
  /** Entity types on the diagram with no row in `POWER_FLOW_SYMBOLS`. */
  unmappedTypes: string[];
  /**
   * Predicates neither the ontology nor this module recognises. A namespace
   * change (`brick:suppliesPowerTo`) makes this non-empty and the diagram thin
   * at the same moment; without the count the second is invisible.
   */
  unknownRelationshipTypes: string[];
  /**
   * Power relationships lost because a breaker on them has no board: it is
   * folded into its container (`hasPart`/`isPartOf`), and with none there is
   * nothing to draw the conductor to.
   */
  uncontainedTerminalLinkCount: number;
  /**
   * Entities carrying power topology in `attributes` rather than
   * `relationships[]`. The schema permits both — 27 `*Attributes` definitions
   * declare `feeds`/`fedBy`/… as a `ValueEnvelope` — and no payload we hold
   * uses that form. A site that did would otherwise render as an empty graph
   * with nothing to explain why.
   */
  attributeTopologyCount: number;
  /**
   * Off-diagram elements of the air and chilled-water paths — anything the
   * twin connects with `feeds`/`isFedBy` (headers, coils, evaporators) that is
   * not itself on the one-line. Spaces at the end of a path are not counted.
   *
   * Reported rather than dropped in silence: a reader who expects to find an
   * air header has to be told it belongs to Floor Plan.
   */
  fluidPathCount: number;
  /** Types behind `fluidPathCount`, for the notice. */
  fluidPathTypes: string[];
}

export interface PowerFlowGraph {
  /** Sorted by id, so downstream stages never depend on pagination order. */
  vertices: PowerFlowVertex[];
  verticesById: Map<string, PowerFlowVertex>;
  /** Sorted by id. */
  links: PowerFlowLink[];
  /**
   * Flow neighbours for **every** entity in the payload, on-diagram or not.
   * The detail panel reads this, so it must not inherit the canvas filter.
   */
  adjacency: Map<string, FlowAdjacency>;
  diagnostics: PowerFlowDiagnostics;
}

/**
 * Attribute keys that carry power topology. Same vocabulary as
 * `flowRelationships.ts`, in the schema's other encoding.
 */
const TOPOLOGY_ATTRIBUTE_KEYS: readonly string[] = [
  'suppliesPowerTo',
  'suppliedBy',
  'primarySource',
  'backupSource',
];

/**
 * Containment predicates and where they store the container. Neither is in the
 * vendored ontology's `Relationship` union; GA-11 publishes both (switchgear →
 * breakers and PQ meters, CRAH → fan and coil).
 */
const CONTAINMENT_TYPES: ReadonlyMap<string, 'forward' | 'inverse'> = new Map([
  ['hasPart', 'forward'],
  ['isPartOf', 'inverse'],
]);

/**
 * Off-schema predicates GA-11 publishes that are never a conductor: Brick's
 * telemetry-point link. Recognised, so the notice does not call them unknown.
 */
const NON_FLOW_TYPES: ReadonlySet<string> = new Set(['hasPoint', 'isPointOf']);

/** Child → container, for every `hasPart`/`isPartOf` in the payload. */
const buildContainers = (relationships: readonly Relationship[]): Map<string, string> => {
  const containerOf = new Map<string, string>();
  for (const rel of relationships) {
    const direction = CONTAINMENT_TYPES.get(rel.type);
    if (!direction) continue;
    // First edge wins, as in `buildHierarchy`: a later conflicting edge in a
    // malformed payload must not unseat a resolvable board.
    const [child, parent] =
      direction === 'forward' ? [rel.target, rel.source] : [rel.source, rel.target];
    if (!containerOf.has(child)) containerOf.set(child, parent);
  }
  return containerOf;
};

/**
 * Bus terminal → the candidate asset that contains it.
 *
 * MOCK-GA10 publishes its switchgear as breakers: the board has no flow edge,
 * every conductor runs `breaker → breaker` inside it and `breaker → UPS / PDU /
 * chiller` out of it (see `BUS_TERMINAL_TYPES`). On a one-line a breaker sits
 * on the tap, not between the bus and the load, so each folds into its
 * container. A terminal with no container is left alone and, not being an
 * asset type, drops off the diagram exactly as it did before.
 */
const buildTerminalContainers = (
  entities: readonly Entity[],
  containers: ReadonlyMap<string, string>,
  candidates: ReadonlyMap<string, Entity>
): Map<string, string> => {
  const containerOf = new Map<string, string>();
  for (const entity of entities) {
    if (!isBusTerminalType(entity.type)) continue;
    const parent = containers.get(entity.id);
    if (parent && candidates.has(parent)) containerOf.set(entity.id, parent);
  }
  return containerOf;
};

/** Above this, a rated voltage is medium voltage on a one-line. */
const MV_THRESHOLD_VOLTS = 1000;

/**
 * Voltage class from a published `ratedVoltage`, or undefined when the asset
 * does not publish one in a form we can read.
 *
 * `12.47 KiloV` and `12470 V` both come out as `mv`; `480 V` as `lv`. A bare
 * number with no unit is read as volts — every payload we hold uses volts for
 * an unadorned rating.
 */
export const voltageClassFromRating = (entity: Entity | undefined): VoltageClass | undefined => {
  const rated = parseQuantity(entity?.attributes?.ratedVoltage);
  if (!rated || (rated.family !== 'V' && rated.family !== 'unknown') || rated.base <= 0) {
    return undefined;
  }
  return rated.base >= MV_THRESHOLD_VOLTS ? 'mv' : 'lv';
};

/**
 * True when an entity could appear on a power diagram at all.
 *
 * `Site` is the root container: via `containsEquipment` it relates to
 * everything, and it is not electrical equipment. Telemetry points are excluded
 * structurally — by being the child end of a telemetry link — rather than by
 * type, so the filter stays correct for types the asset allow-list has not
 * caught up with.
 *
 * Note this is only *candidacy*. Being on the diagram additionally requires a
 * power connection, which is decided below.
 */
export const isPowerCandidate = (entity: Entity, telemetryIds: ReadonlySet<string>): boolean =>
  isAssetEntityType(entity.type) &&
  cleanEntityType(entity.type) !== 'Site' &&
  !telemetryIds.has(entity.id) &&
  !isNonElectricalType(cleanEntityType(entity.type));

/**
 * Build the flow graph.
 *
 * Deterministic: identical content produces an identical graph however the two
 * input arrays were ordered. Asserted by shuffling them in the tests, because
 * the details endpoint paginates and page order is not stable.
 */
export const buildPowerFlowGraph = (
  entities: readonly Entity[],
  relationships: readonly Relationship[]
): PowerFlowGraph => {
  const telemetryIds = getTelemetryEntityIds(relationships as Relationship[]);
  const candidates = new Map<string, Entity>();
  // Every id the payload carries, so "is this endpoint absent from entities[]"
  // is a set lookup rather than a scan per relationship — a 3,000-entity site
  // has ~3,000 flow edges, and the scan made that quadratic.
  const publishedIds = new Set<string>();
  const terminalIds = new Set<string>();
  for (const entity of entities) {
    publishedIds.add(entity.id);
    if (isPowerCandidate(entity, telemetryIds)) candidates.set(entity.id, entity);
    if (isBusTerminalType(entity.type)) terminalIds.add(entity.id);
  }

  const adjacency = buildFlowAdjacency(relationships);
  const containers = buildContainers(relationships);
  const containerOf = buildTerminalContainers(entities, containers, candidates);
  const resolve = (id: string): string => containerOf.get(id) ?? id;

  // --- Links, and the assets they put on the diagram ---------------------
  // One link per ordered pair. A payload publishing both `feeds` and `fedBy`
  // for the same connection yields one conductor, not two stacked on each
  // other.
  const linkTypes = new Map<string, { relTypes: Set<string>; via: Set<string> }>();
  // Keyed by endpoint pair: GA-11 publishes each conductor both ways, and one
  // missing conductor is one finding.
  const danglingLinks = new Set<string>();
  const uncontainedTerminalLinks = new Set<string>();

  for (const rel of relationships) {
    const flow = normalizeFlowRelationship(rel);
    if (!flow) continue;

    // A breaker's edge is its board's edge.
    const source = resolve(flow.source);
    const target = resolve(flow.target);

    // An endpoint outside the candidate set is either off-diagram by design
    // (a telemetry point, the site node) or genuinely absent from `entities[]`.
    // Only the second is a data problem, so only it is counted.
    if (!candidates.has(source) || !candidates.has(target)) {
      if (!publishedIds.has(flow.source) || !publishedIds.has(flow.target)) {
        danglingLinks.add(`${flow.source}->${flow.target}`);
      } else if (
        (terminalIds.has(flow.source) && !containerOf.has(flow.source)) ||
        (terminalIds.has(flow.target) && !containerOf.has(flow.target))
      ) {
        uncontainedTerminalLinks.add(`${flow.source}->${flow.target}`);
      }
      continue;
    }

    // A self-loop is not drawable and not meaningful on a one-line. After the
    // fold this is also every `breaker → breaker` edge inside one board.
    if (source === target) continue;

    const key = `${source}->${target}`;
    let entry = linkTypes.get(key);
    if (!entry) {
      entry = { relTypes: new Set(), via: new Set() };
      linkTypes.set(key, entry);
    }
    entry.relTypes.add(flow.relType);
    if (source !== flow.source) entry.via.add(flow.source);
    if (target !== flow.target) entry.via.add(flow.target);
  }

  const links: PowerFlowLink[] = Array.from(linkTypes.entries())
    .map(([id, { relTypes, via }]) => {
      const [source, target] = id.split('->');
      return {
        id,
        source,
        target,
        relTypes: Array.from(relTypes).sort(),
        via: Array.from(via).sort(),
      };
    })
    .sort((a, b) => (a.id < b.id ? -1 : 1));

  // --- Vertices ----------------------------------------------------------
  // On the diagram iff it has at least one drawable link. A source type with no
  // downstream edge is a generator nobody wired up in the payload: there is
  // nothing to draw it connected to, so it is reported as unconnected rather
  // than floated on the canvas with no conductor.
  // Neighbours come from the drawn links, not the raw adjacency: after the
  // fold a UPS's upstream is its board, not the breaker the payload named.
  const onDiagram = new Set<string>();
  const inOf = new Map<string, string[]>();
  const outOf = new Map<string, string[]>();
  for (const link of links) {
    onDiagram.add(link.source);
    onDiagram.add(link.target);
    outOf.set(link.source, [...(outOf.get(link.source) ?? []), link.target]);
    inOf.set(link.target, [...(inOf.get(link.target) ?? []), link.source]);
  }

  const vertices: PowerFlowVertex[] = Array.from(onDiagram)
    .sort()
    .map(id => {
      const entity = candidates.get(id);
      const entityType = entity?.type ?? '';
      const rule = symbolRuleFor(entityType);
      return {
        id,
        entityType,
        role: rule.role,
        symbol: rule.symbol,
        // The nameplate outranks the symbol table: a published `ratedVoltage`
        // says which side of the transformer an asset is on, and the table can
        // only guess from its type.
        voltageClass: voltageClassFromRating(entity) ?? rule.voltageClass,
        in: (inOf.get(id) ?? []).sort(),
        out: (outOf.get(id) ?? []).sort(),
      };
    });

  const verticesById = new Map(vertices.map(vertex => [vertex.id, vertex]));

  // --- Diagnostics -------------------------------------------------------
  // The air and water paths, by the predicate that carries them rather than a
  // type list: whatever sits on a `feeds` edge and is not itself drawn.
  const fluidIds = new Set<string>();
  for (const rel of relationships) {
    if (!isFluidRelationshipType(rel.type)) continue;
    fluidIds.add(rel.source);
    fluidIds.add(rel.target);
  }
  const entitiesById = new Map(entities.map(entity => [entity.id, entity]));
  // A component drawn as its container (a CRAH's fan and coil) is on the diagram.
  const drawnInContainer = (id: string): boolean => {
    const container = containers.get(id);
    return container !== undefined && onDiagram.has(container);
  };
  const fluidPathByType = new Map<string, number>();
  for (const id of Array.from(fluidIds)) {
    const entity = entitiesById.get(id);
    if (
      !entity ||
      onDiagram.has(id) ||
      // An undrawn power candidate is reported as unconnected below instead.
      (candidates.has(id) && !drawnInContainer(id)) ||
      telemetryIds.has(id) ||
      isSpaceType(entity.type)
    ) {
      continue;
    }
    const type = cleanEntityType(entity.type);
    fluidPathByType.set(type, (fluidPathByType.get(type) ?? 0) + 1);
  }

  // "No power connections" is about assets a reader would expect on the
  // one-line. A component drawn as its container is not missing. A power
  // candidate on a `feeds` edge still is: a pump moves water
  // but draws power, so the fluid edge must not hide a missing conductor.
  const unconnectedByType = new Map<string, number>();
  for (const [id, entity] of Array.from(candidates.entries())) {
    if (onDiagram.has(id) || drawnInContainer(id)) continue;
    unconnectedByType.set(entity.type, (unconnectedByType.get(entity.type) ?? 0) + 1);
  }

  const unconnected: TypeCount[] = Array.from(unconnectedByType.entries())
    .map(([entityType, count]) => ({ entityType, count }))
    .sort((a, b) => b.count - a.count || (a.entityType < b.entityType ? -1 : 1));

  const unmappedTypes = Array.from(
    new Set(vertices.map(v => v.entityType).filter(type => !hasSymbolRule(type)))
  ).sort();

  // "Unknown" means this module has no rule for it: the off-schema predicates
  // GA-11 publishes (`isFedBy`, `hasPart`/`isPartOf`, `hasPoint`) are known.
  const unknownRelationshipTypes = Array.from(
    new Set(
      relationships
        .map(rel => rel.type)
        .filter(
          type =>
            !isKnownRelationshipType(type) &&
            !isFlowRelationshipType(type) &&
            !isFluidRelationshipType(type) &&
            !CONTAINMENT_TYPES.has(type) &&
            !NON_FLOW_TYPES.has(type)
        )
    )
  ).sort();

  let attributeTopologyCount = 0;
  for (const entity of Array.from(candidates.values())) {
    if (TOPOLOGY_ATTRIBUTE_KEYS.some(key => entity.attributes?.[key] != null)) {
      attributeTopologyCount += 1;
    }
  }

  return {
    vertices,
    verticesById,
    links,
    adjacency,
    diagnostics: {
      unconnected,
      unconnectedCount: unconnected.reduce((total, row) => total + row.count, 0),
      danglingLinkCount: danglingLinks.size,
      uncontainedTerminalLinkCount: uncontainedTerminalLinks.size,
      unmappedTypes,
      unknownRelationshipTypes,
      attributeTopologyCount,
      fluidPathCount: Array.from(fluidPathByType.values()).reduce(
        (total, count) => total + count,
        0
      ),
      fluidPathTypes: Array.from(fluidPathByType.keys()).sort(),
    },
  };
};
